import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import {
  CalibrationBlockDto,
  CalibrationBlockOptionDto,
  CalibrationBlockType,
  CalibrationInformation,
  CalibrationResponse,
  CalibrationType,
  CarriageLocation,
  EmployeeResponse,
  FormExaminationFaultDto,
  FormPartDto,
  FormRectificationWorkDto,
  LastOpenedFeature,
  LoadcellCommentDto,
  LoadcellFileDto,
  LoadcellInformationResponse,
  LoadCellPhotoResponse,
  LoadcellResponse,
  MachineRating,
  ManufacturerSpec,
  PressureTestResult,
  PriorityStatus,
  SiteOrigin,
  Status,
  TestMachine,
  YesNoNa,
} from "app/api/models";
import {
  CalibrationPriceService,
  CalibrationService,
  EmployeeService,
  Form10AService,
  LoadCellPhotoService,
  PressureTestService,
  RepairsAndCalibrationsService,
} from "app/api/services";
import StatusHelper from "../../../shared/helpers/status-helper";
import { GenerateQrCodeDialogComponent } from "app/views/calibrations/views/form10a/generate-qr-code-dialog/generate-qr-code-dialog.component";
import { showSlidesInImageCarousel } from "../../../shared/helpers/slide-helper";
import { PrintCalibrationCertificateDialogComponent } from "./print-calibration-certificate-dialog/print-certificate-dialog.component";
import { PrintQuotationDialogComponent } from "./print-quotation-dialog/print-quotation-dialog.component";
import { RotCertificateComponent } from "./rot-certificate/rot-certificate.component";
import { UserRoleService } from "../../../shared/services/auth/user-role.service";
import { SelectForQuoteDialogComponent } from "./select-for-quote-dialog/select-for-quote-dialog.component";
import { SelectForPressureDialogComponent } from "./select-for-pressure-dialog/select-for-pressure-dialog.component";
import { PrintPressureTestDailogComponent } from "./print-pressure-test-dailog/print-pressure-test-dailog.component";
import { CalibrationFormDialogComponent } from "../calibration-form-dialog/calibration-form-dialog.component";
import { Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MatRadioChange, MatRadioModule } from "@angular/material/radio";
import { SelectForBulkQrDialogComponent } from "./select-for-bulk-qr-dialog/select-for-bulk-qr-dialog.component";
import { MatCheckboxChange, MatCheckboxModule } from "@angular/material/checkbox";
import { CommonModule } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatSelectChange, MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DLMSharedModule } from "app/views/calibrations/shared/shared.module";
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxPrintModule } from "ngx-print";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { Subject, takeUntil } from "rxjs";
import { DLMSharedComponentsModule } from "app/views/calibrations/shared/components/shared-components.module";
import { MatDrawer, MatSidenavModule } from "@angular/material/sidenav";
import { GenericCommentDto } from "app/core/components/comments/comments.component";
import { SharedComponentsModule } from "app/core/components/shared-components.module";
import { UploadFilePopupComponent } from "app/views/admin/installation/documentations/upload-file-popup/upload-file-popup.component";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { sortOnProperty, sumList } from "app/app.component";
import { LocalStoreService } from "app/core/services/local-store.service";
import { AppAuthService } from "app/api/services/cookie-jwt-auth/app-auth.service";
import { DLMUploadImageDialogComponent } from "app/views/calibrations/shared/components/dlm-upload-image-dialog/dlm-upload-image-dialog.component";

export enum Tab {
  Comments = 1, //count from 1 so always truthy
  Customer,
  Preliminary,
  Fault,
  Calibration,
  DispatchNotes,
  History,
  Emails,
  // Documents,
}

export interface StatusListOption {
  value: number;
  name: string;
}

@Component({
  selector: "app-cell-information-dialog",
  templateUrl: "./cell-information-dialog.component.html",
  styleUrls: ["./cell-information-dialog.component.scss", "./../../../_styles/calibration-overrides.scss"],
  standalone: true,
  imports: [
    CommonModule,
    DLMSharedModule,
    DLMSharedComponentsModule,
    MatSidenavModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    FlexLayoutModule,
    DragScrollModule,
    NgxPrintModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
    SharedComponentsModule, //for comments
  ]
})
export class CellInformationDialogComponent implements OnInit, OnDestroy {
  model: LoadcellInformationResponse;
  loaded: boolean = false;

  accessLevel: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isBasic: boolean;

  selectedTab: Tab = Tab.Preliminary;
  selectedCalibrationIndex: number = 0;

  public Tab = Tab;
  public priority = PriorityStatus;
  public status = Status;
  public YesNoNa = YesNoNa;

  Math = Math;
  ManufacturerSpec = ManufacturerSpec;
  CarriageLocation = CarriageLocation;

  otherItemsExpanded: boolean = false;
  preliminaryExpanded: boolean = false;
  rectificationExpanded: boolean = false;
  bridgeLegExpanded: boolean = false;
  connectorExpanded: boolean = false;

  statusList: StatusListOption[] = [];
  employees: EmployeeResponse[] = [];

  // DB-driven block options (admin-managed). A block's display name is resolved from its
  // CalibrationBlockOptionId against this list; the raw enum name is only a fallback for
  // pre-migration rows with no FK.
  blockOptions: CalibrationBlockOptionDto[] = [];

  photoFileName: any;
  slideIndex: number = 0;
  loadCellPhotos: LoadCellPhotoResponse[] = [];
  files: File[] = [];
  urls: SafeUrl[] = [];
  form10aId: number;

  attachments: LoadcellFileDto[] = [];

  editingFault: FormExaminationFaultDto;
  editingRectification: FormRectificationWorkDto;

  public genericComments: GenericCommentDto[];

  @ViewChild('drawer') drawer: MatDrawer;
  drawerMode: 'over' | 'side' = 'side';
  drawerOpened: boolean = true;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  panels = [
    {
      id: Tab.Comments,
      title: 'Comments',
      icon: 'heroicons_outline:chat-bubble-bottom-center-text'
    },
    {
      id: Tab.Customer,
      title: 'Customer',
      icon: 'mat_outline:person'
    },
    {
      id: Tab.Preliminary,
      title: 'Preliminary Report & Quotation',
      icon: 'heroicons_outline:document-text'
    },
    {
      id: Tab.Fault,
      title: 'Fault Report Details',
      icon: 'heroicons_outline:exclamation-triangle'
    },
    {
      id: Tab.Calibration,
      title: 'Calibration Test Results',
      icon: 'heroicons_outline:clipboard-document-check'
    },
    {
      id: Tab.History,
      title: 'Activity Log',
      icon: 'mat_outline:restore'
    },
    {
      id: Tab.Emails,
      title: 'Email Trail',
      icon: 'heroicons_outline:envelope'
    },
    {
      id: Tab.DispatchNotes,
      title: 'Documents',
      icon: 'heroicons_outline:document'
    },
  ];

  constructor(
    public dialogRef: MatDialogRef<CellInformationDialogComponent>,
    private cdr: ChangeDetectorRef,
    public repairsService: RepairsAndCalibrationsService,
    private dialog: MatDialog,
    private loadCellPhotoService: LoadCellPhotoService,
    private sanitizer: DomSanitizer,
    private userRoleService: UserRoleService,
    private employeeService: EmployeeService,
    private form10aService: Form10AService,
    private pressureTestService: PressureTestService,
    private calibrationService: CalibrationService,
    private calibrationPriceService: CalibrationPriceService,
    private router: Router,
    private snack: MatSnackBar,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseConfirmationService: FuseConfirmationService,
    private ls: LocalStoreService,
    public appAuth: AppAuthService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    if (this.data != null) {
      this.accessLevel = this.userRoleService.getCurrentUserRole();
      this.isAdmin = this.appAuth.isAdmin();
      this.isSuperAdmin = this.appAuth.isSuperAdmin();
      this.isBasic = this.appAuth.isBasic();

      this.loadEmployees();
      this.getLoadcellInfo();
      this.loadBlockOptions();

      for (let element in Object.keys(Status).filter(
        (x) => !isNaN(Number(x))
      )) {
        this.statusList.push({
          value: Number(element),
          name: this.getStatus(Number(element)),
        });
      }
    }

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(({matchingAliases}) =>
        {
            // Set the drawerMode and drawerOpened
            if ( matchingAliases.includes('lg') )
            {
                this.drawerMode = 'side';
                this.drawerOpened = true;
            }
            else
            {
                this.drawerMode = 'over';
                this.drawerOpened = false;
            }

            // Mark for check
            this.cdr.markForCheck();
        });
  }

  ngOnDestroy(): void
  {
      // Unsubscribe from all subscriptions
      this._unsubscribeAll.next(null);
      this._unsubscribeAll.complete();
  }

  getLoadcellInfo() {
    this.repairsService
      .apiRepairsAndCalibrationsLoadcellInformationGet$Json({ loadcellId: this.data.loadcellId })
      .subscribe((res) => {
        this.model = res.data;
        this.form10aId = res.data.form10a.form10aId;
        if (this.model.form10a.form10aId != null) {
          this.populatePhotos();
          if (this.loadCellPhotos.length > 0) {
            this.showSlides(this.slideIndex);
          }
        }

        this.model.loadcell.history = sortOnProperty(this.model.loadcell.history ?? [], 'timeStamp', false);
        this.model.loadcell.emailTrails = sortOnProperty(this.model.loadcell.emailTrails ?? [], 'date', false);
        for(var email of this.model.loadcell.emailTrails)
          email.body = email.body.replace('\n', '<br/>');
        
        this.getIncompleteCalibration();

        this.genericComments = this.getGenericComments(this.model.loadcell.userComments);
        this.loadFiles();

        this.loaded = true;
        this.cdr.detectChanges();

        this.setCalibrationFee();

        this.loadInitialFeature();

        //don't show calibrations tab if no calibrations on model
        if(!this.model.calibrations?.length)
          this.panels = this.panels.filter(x => x.id != Tab.Calibration);
      });
  }

  getIncompleteCalibration() {
    if(!this.model?.loadcell?.calibrationId)
      return;

    this.calibrationService.apiCalibrationCalibrationGet$Json$Response({calibrationId: this.model.loadcell.calibrationId}).subscribe(res => {
      if(!res?.body?.data?.calibration) return;

      this.model.calibrations.push(res.body.data.calibration);
    });
  }

  /*refresh the data */
  refreshLoadcellModel(): Promise<void> {
    return this.repairsService
      .apiRepairsAndCalibrationsLoadcellInformationGet$Json({ loadcellId: this.data.loadcellId })
      .toPromise()
      .then((res) => {
        if (res?.data) {
          this.model = res.data;
          this.model.loadcell.history = sortOnProperty(this.model.loadcell.history ?? [], 'timeStamp', false);
          this.model.loadcell.emailTrails = sortOnProperty(this.model.loadcell.emailTrails ?? [], 'date', false);
          for(var email of this.model.loadcell.emailTrails)
            email.body = email.body.replace('\n', '<br/>');

          this.getIncompleteCalibration();
          this.cdr.detectChanges();
        }
      });
  }

  loadInitialFeature() {
    console.log("initial feature", this.data.initialFeature);

    if(this.data.initialFeature) {
      switch(this.data.initialFeature as LastOpenedFeature) {
        case LastOpenedFeature.CommentsTab:
          this.selectedTab = Tab.Comments;
          break;
        case LastOpenedFeature.CustomerTab:
          this.selectedTab = Tab.Customer;
          break;
        case LastOpenedFeature.PreliminaryTab:
          this.selectedTab = Tab.Preliminary;
          break;
        case LastOpenedFeature.FaultTab:
          this.selectedTab = Tab.Fault;
          break;
        case LastOpenedFeature.CalibrationTab:
          this.selectedTab = Tab.Calibration;
          break;
        case LastOpenedFeature.DispatchNotesTab:
          this.selectedTab = Tab.DispatchNotes;
          break;

        case LastOpenedFeature.QuotationCertificate:
          this.openQuotationDialog();
          break;
        case LastOpenedFeature.ExaminationReport:
          this.openExaminationReportDialog();
          break;
        case LastOpenedFeature.CalibrationCertificate:
          this.openPrintDialog()
          break;
        case LastOpenedFeature.EditCalibration:
          // this.editCalibration(); //todo needs index
          break;
      }
    }
  }

  loadEmployees() {
    this.employeeService.apiEmployeeEmployeesGet$Json$Response().subscribe(res => {
      if(!res?.body?.data) return;

      this.employees = res.body.data;
    });
  }

  updateEmployee(event: MatSelectChange) {
    this.model.inspectedBy = event.value;

    var employee = this.employees.find(x => x.firstName + ' ' + x.lastName == event.value);

    if(!employee) return;

    var user = this.appAuth.getUser();

    this.repairsService
      .apiRepairsAndCalibrationsUpdateInspectedByPost$Json({
        employeeId: employee.id,
        loadcellId: this.model?.loadcell?.loadcellId,
        userName: `${user.firstName} ${user.lastName}`,
      })
      .subscribe();
    this.cdr.detectChanges();
  }

  updateEmployeeById(userId: number) {
    var user = this.appAuth.getUser();
    this.repairsService
      .apiRepairsAndCalibrationsUpdateInspectedByPost$Json({
        employeeId: userId,
        loadcellId: this.model?.loadcell?.loadcellId,
        userName: `${user.firstName} ${user.lastName}`,
      })
      .subscribe();
  }

  private getCalibrationPrices() {
    return this.calibrationPriceService.apiCalibrationPriceCalibrationPricesGet$Json({
      archived: false,
    });
  }

  setCalibrationFee() {
    console.log("calibration fee", this.model.form10a.calibrationFee);

    // Early return if form10a or loadCellType is not available
    if (!this.model?.form10a) {
      console.log("No form10a available");
      return;
    }

    // If we already have a calibration fee OR no calibrations, return early
    if (this.model.form10a.calibrationFee || !this.model.calibrations?.length) {
      return;
    }

    // Get the first calibration if available
    const calibration = this.model.calibrations?.[0];
    if (!calibration) {
      console.log("No calibration available");
      return;
    }

    // Extract and clean pressure and SWL values
    const pressureStr = (calibration.pressureRating || "").replace(/[^0-9]/g, "");
    const swlStr = (calibration.swl || "").replace(/[^0-9]/g, "");

    const pressure = pressureStr ? parseInt(pressureStr) : 0;
    const swl = swlStr ? parseInt(swlStr) : 0;

    if (!(pressure || swl)) {
      console.log("No pressure or SWL available");
      return;
    }

    this.getCalibrationPrices().subscribe((res) => {
      if (!res?.data) {
        console.log("No calibration prices available");
        return;
      }

      const calibrationPrices = res.data;
      console.log("Calibration prices:", calibrationPrices);

      // Safely access loadCellType
      const loadCellTypeId = this.model.form10a?.loadCellType?.id;
      console.log("loadcell type id", loadCellTypeId);

      let prices = [];

      if (pressure) {
        console.log("pressure", pressure);
        prices = calibrationPrices.filter(
          (x) =>
            x.calibrationType === CalibrationType.Pressure &&
            pressure >= x.fromValue &&
            pressure <= x.toValue
        );
        if (!prices.length) {
          console.log("No matching pressure prices found");
          return;
        }
      } else if (swl) {
        console.log("swl", swl);
        let convertedSwl = swl;
        if (calibration.swl.toLowerCase().includes("lb")) {
          console.log("lbs - converting to tonnes");
          convertedSwl /= 2205;
        } else if (calibration.swl.toLowerCase().includes("kg")) {
          console.log("kgs - converting to tonnes");
          convertedSwl /= 1000;
        }
        console.log("converted swl", convertedSwl);

        prices = calibrationPrices.filter(
          (x) =>
            x.calibrationType === CalibrationType.Regular &&
            convertedSwl >= x.fromValue &&
            convertedSwl <= x.toValue
        );
        if (!prices.length) {
          console.log("No matching SWL prices found");
          return;
        }
      }

      // Try to find a price specific to the loadcell type
      if (loadCellTypeId) {
        const typePrices = prices.filter(
          (x) => x.loadCellTypeId === loadCellTypeId
        );
        console.log("type specific prices", typePrices);

        if (typePrices.length) {
          this.model.form10a.calibrationFee = typePrices[0].price;
          this.updateForm10A();
          console.log("Set type-specific calibration fee:", this.model.form10a.calibrationFee);
          return;
        }
      }

      // Fall back to typeless prices
      const typelessPrices = prices.filter((x) => x.loadCellTypeId == null);
      if (!typelessPrices.length) {
        console.log("No typeless prices found");
        return;
      }

      console.log("typeless prices", typelessPrices);
      this.model.form10a.calibrationFee = typelessPrices[0].price;
      this.updateForm10A();
      console.log("Set typeless calibration fee:", this.model.form10a.calibrationFee);
    });
  }

  selectTab(tab: Tab) {
    this.selectedTab = tab;

    // Close the drawer on 'over' mode
    if ( this.drawerMode === 'over' )
    {
        this.drawer.close();
    }

    switch(tab) {
      case Tab.Comments:
        this.setLastOpenedFeature(LastOpenedFeature.CommentsTab);
        break;
      case Tab.Customer:
        this.setLastOpenedFeature(LastOpenedFeature.CustomerTab);
        break;
      case Tab.Preliminary:
        this.setLastOpenedFeature(LastOpenedFeature.PreliminaryTab);
        break;
      case Tab.Fault:
        this.setLastOpenedFeature(LastOpenedFeature.FaultTab);
        break;
      case Tab.Calibration:
        this.setLastOpenedFeature(LastOpenedFeature.CalibrationTab);
        break;
      case Tab.DispatchNotes:
        this.setLastOpenedFeature(LastOpenedFeature.DispatchNotesTab);
        break;
    }
  }

  selectCalibration(index: number) {
    this.selectedCalibrationIndex = index;
  }

  editCalibration(index: number) {
    this.setLastOpenedFeature(LastOpenedFeature.EditCalibration);

    var calibrationId = this.model?.calibrations[index]?.calibrationId;

    var dialog = this.dialog.open(CalibrationFormDialogComponent, {
      panelClass: "calibration-form-dialog",
      data: { loadcellId: null, calibrationId: calibrationId, disableEdit: !this.appAuth.isSuperAdmin() },
    });

    dialog.afterClosed().subscribe(async (result) => {
      console.log("calib form close - cell info", result);

      await this.refreshLoadcellModel();

      var cell: LoadcellResponse = Object.assign({}, this.model.loadcell);

      if (result?.calibration?.isComplete) {
        if (result.setPressureTestMachine) {
          // Calibration done, but it still needs pressure testing. Send a coherent,
          // non-regressing pair: keep it "in progress" on Servicing and move it to the
          // Pressure Test lane. Crucially never send BookedIn here — the API turns
          // BookedIn into machine=Unassigned, which would discard the Pressure Test
          // lane and strand the loadcell (see loadcell history bug, Jun 2026).
          cell.status = Status.InCalibration;
          cell.machine = TestMachine.PressureTest;
        } else if (this.model.loadcell.status === Status.InCalibration) {
          // Calibration report complete — advance both boards.
          cell.machine = TestMachine.CalReportComplete;
          cell.status = Status.CalReportComplete;
        }

        cell.isFromCalibrationEdit = true;
        cell.isPressureTestEdit = result.calibration.fromPressureTest;
        this.updateLoadcells(null, [cell]);
      }
      else
        this.getLoadcellInfo();
    });
  }

  updateLoadcells(
    navLink: string = null,
    list: LoadcellResponse[]
  ) {
    var user = this.appAuth.getUser();
    this.repairsService
      .apiRepairsAndCalibrationsEditLoadcellsPost$Json({ body: list, userName: `${user.firstName} ${user.lastName}`, siteOrigin: SiteOrigin.LoadcellInformation })
      .subscribe((res) => {
        this.getLoadcellInfo();
        this.cdr.detectChanges();

        if (navLink) this.router.navigate([navLink]);
      });
  }

  hasUnsyncedCalibration(index: number) {
    var calibrationId = this.model?.calibrations[index]?.calibrationId;
    var calibration = this.ls.getStoredCalibration(calibrationId);

    return calibration != null;
  }

  saveOfflineCalibration(index: number) {
    var calibrationId = this.model?.calibrations[index]?.calibrationId;
    var calibration = this.ls.getStoredCalibration(calibrationId);

    this.calibrationService.apiCalibrationEditCalibrationPost$Json({body: calibration}).subscribe({
        next: (res) => {
            if (!res?.data) {
                this.snack.open('Error saving calibration', 'OK', {
                    duration: 3000
                });
                return;
            }
            this.snack.open('Calibration successfully synced to Nexus.', 'OK', {
                duration: 3000
            });
            this.ls.clearCalibrationsForId(calibration.calibration.calibrationId);

            var currentUserId = this.appAuth.getUser()?.id;
            if(currentUserId)
              this.updateEmployeeById(currentUserId);
        },
        error: (error) => {
            this.snack.open('Error saving calibration', 'OK', {
                duration: 3000
            });
        }
    });
  }

  toggleCollapseOtherItems() {
    this.otherItemsExpanded = !this.otherItemsExpanded;
  }

  toggleCollapsePreliminary() {
    this.preliminaryExpanded = !this.preliminaryExpanded;
  }

  toggleCollapseRectification() {
    this.rectificationExpanded = !this.rectificationExpanded;
  }

  toggleCollapseBridgeLeg() {
    this.bridgeLegExpanded = !this.bridgeLegExpanded;
  }

  toggleCollapseConnector() {
    this.connectorExpanded = !this.connectorExpanded;
  }

  updateCustomerRef(event: any) {
    this.model.loadcell.customerRef = event.target.value;
    this.updateLoadcell();
  }

  isEditingFault(fault: FormExaminationFaultDto) {
    return (
      this.editingFault?.formExaminationFaultId == fault?.formExaminationFaultId
    );
  }

  toggleEditFault(fault: FormExaminationFaultDto, save: boolean) {
    if (!this.isEditingFault(fault)) {
      this.editingFault = { ...fault };
    } else {
      if (!save) {
        this.editingFault = null;
        return;
      }

      this.form10aService
        .apiForm10AUpdateExaminationFaultPost$Json({ body: this.editingFault })
        .subscribe((res) => {
          if (!res?.data) {
            this.snack.open("Failed to update fault", "OK", { duration: 2000 });
            return;
          }

          this.model.form10a.formExaminationFaults.find(
            (x) =>
              x.formExaminationFaultId ==
              this.editingFault?.formExaminationFaultId
          ).comment = this.editingFault?.comment;
          this.editingFault = null;
        });
    }
  }

  isEditingRectification(rectificationWork: FormRectificationWorkDto) {
    return (
      this.editingRectification?.formRectificationId ==
      rectificationWork?.formRectificationId
    );
  }

  toggleEditRectification(
    rectificationwork: FormRectificationWorkDto,
    save: boolean
  ) {
    if (!this.isEditingRectification(rectificationwork)) {
      this.editingRectification = { ...rectificationwork };
    } else {
      if (!save) {
        this.editingRectification = null;
        return;
      }

      this.form10aService
        .apiForm10AUpdateRectificationWorkPost$Json({ body: this.editingRectification })
        .subscribe((res) => {
          if (!res?.data) {
            this.snack.open(
              "Failed to update rectification work required",
              "OK",
              { duration: 2000 }
            );
            return;
          }

          this.model.form10a.formRectificationWork.find(
            (x) =>
              x.formRectificationId ==
              this.editingRectification?.formRectificationId
          ).comment = this.editingRectification?.comment;
          this.editingRectification = null;
        });
    }
  }

  updateCalibrationFee($event: any) {
    var value = $event.target.value;
    value = value.replace("£", "").replace(",", "");

    if (isNaN(value)) value = 0;

    value = parseFloat(parseFloat(value).toFixed(2));

    this.model.form10a.calibrationFee = value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update calibration fee", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updateCarriageFee($event: any) {
    var value = $event.target.value;
    value = value.replace("£", "").replace(",", "");

    if (isNaN(value)) value = 0;

    value = parseFloat(parseFloat(value).toFixed(2));

    this.model.form10a.carriage = value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update carriage fee", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updateCarriageLocation(event: MatRadioChange) {
    this.model.form10a.carriageLocation = event.value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update carriage location", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updateExWork(event: MatCheckboxChange) {
    this.model.form10a.exWork = event.checked;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update Ex-Work", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updatePressureTestCost($event: any) {
    var value = $event.target.value;
    value = value.replace("£", "").replace(",", "");

    if (isNaN(value)) value = 0;

    value = parseFloat(parseFloat(value).toFixed(2));

    this.model.form10a.pressureTestCost = value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update pressure test cost", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updateThirdPartyCalibrationClient($event: any) {
    var value = $event.target.value;

    this.model.form10a.thirdPartyCalibrationClient = value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update third party calibration client", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updatePurchaseOrder($event: any) {
    var value = $event.target.value;

    this.model.form10a.purchaseOrder = value;

    this.updateForm10A().subscribe((res) => {
      if (!res?.data) {
        this.snack.open("Failed to update purchase order", "OK", {
          duration: 2000,
        });
      }

      this.cdr.detectChanges();
    });
  }

  updateForm10A() {
    return this.form10aService.apiForm10AEditForm10APost$Json({ body: this.model.form10a });
  }

  getMachineRatingText(machineRating: MachineRating) {
    switch (machineRating) {
      case MachineRating.One:
        return "25T";
      case MachineRating.Sixty:
        return "60T";
      case MachineRating.OneHundred:
        return "100T";
      case MachineRating.ThreeThousand:
        return "3000kN";
      case MachineRating.Other:
        return "Other";
      default:
        return "N/A";
    }
  }

  getResolutionScale(
    calibration: CalibrationInformation,
    isAdditional: boolean = false
  ) {
    var value = isAdditional
      ? calibration?.additionalResolution ?? 0
      : calibration?.resolution ?? 0;

    // Convert to a string
    const valueAsString = value.toString();

    // Check if there is a decimal point in the string
    if (valueAsString.includes(".")) {
      // The number of decimal places is the length of the substring after the decimal point
      var scale = valueAsString.split(".")[1].length;
      return scale;
    } else {
      return 0;
    }
  }

  getRoundingFactor(scale: number) {
    var factor = Math.pow(10, scale);
    return factor;
  }

  getScaledValue(
    value: number,
    calibration: CalibrationInformation,
    isAdditional: boolean = false
  ) {
    var scale = this.getResolutionScale(calibration, isAdditional);
    var factor = this.getRoundingFactor(scale);
    var result = (Math.round(value * factor) / factor).toFixed(
      scale > 0 ? scale : 0
    );

    return result;
  }

  printQRPopup() {
    if (this.model.form10a.form10aId != null) {
      var dialog = this.dialog.open(GenerateQrCodeDialogComponent, {
        panelClass: "generate-qr-code-dialog",
        data: { form10a: this.model.form10a },
      });
    }
  }

  bulkPrintQRPopup() {
    // var id = this.model.loadcell.loadcellId;
    // var ref = this.model.loadcell.bookingRef;

    var dialog = this.dialog.open(SelectForBulkQrDialogComponent, {
      panelClass: "select-for-bulk-qr-dialog",
      data: {
        loadcellId: this.model?.loadcell.loadcellId,
      },
    });

    dialog.afterClosed().subscribe((list) => {
      if (list) {
        var dialog = this.dialog.open(GenerateQrCodeDialogComponent, {
          panelClass: "generate-qr-code-dialog",
          data: { form10as: list },
        });

        dialog.afterClosed().subscribe((form10as) => {
          if (form10as) {
            console.log("chosen form10as", form10as);
          }
        });
      }
    });
  }

  openQuotationDialog() {
    this.setLastOpenedFeature(LastOpenedFeature.QuotationCertificate);

    if (!this.model?.form10a?.quote) {
      var dialog = this.dialog.open(SelectForQuoteDialogComponent, {
        panelClass: "select-for-quote-dialog",
        data: {
          customerId: this.model?.form10a?.formCustomer?.customerId,
          loadcellId: this.model?.loadcell.loadcellId,
        },
      });

      dialog.afterClosed().subscribe((list) => {
        if (list) {
          // Pass a list of form10a Ids to give quotes
          this.form10aService
            .apiForm10AQuotePost$Json({ body: list })
            .subscribe((quoteId) => {
              if (!quoteId) return;

              this.model.form10a.quote = {
                quoteId: quoteId?.data,
              };

              this.dialog.open(PrintQuotationDialogComponent, {
                panelClass: "print-quotation-dialog",
                data: {
                  quoteId: this.model?.form10a?.quote?.quoteId,
                },
              });
            });
        }
      });
    } else {
      this.dialog.open(PrintQuotationDialogComponent, {
        panelClass: "print-quotation-dialog",
        data: {
          quoteId: this.model?.form10a?.quote?.quoteId,
        },
      });
    }
  }

  openPrintDialog() {
    this.setLastOpenedFeature(LastOpenedFeature.CalibrationCertificate);

    var calibration = this.model?.calibrations[this.selectedCalibrationIndex];

    if (calibration) {
      var dialog = this.dialog.open(PrintCalibrationCertificateDialogComponent, {
        panelClass: "print-certificate-dialog",
        data: { calibration: calibration, loadcellInfo: this.model },
      });
    }
  }

  openExaminationReportDialog() {
    this.setLastOpenedFeature(LastOpenedFeature.ExaminationReport);

    var calibration = this.model?.calibrations[this.selectedCalibrationIndex];

    if (calibration) {
      var dialog = this.dialog.open(RotCertificateComponent, {
        panelClass: "rot-certificate-dialog",
        data: { calibration: calibration, loadcellInfo: this.model },
      });
    }
  }

  calibrationHasPressureTest() {
    return (
      this.model?.calibrations[this.selectedCalibrationIndex]
        .pressureTestResult != PressureTestResult.NA
    );
  }

  openPressureTestDialog() {
    var pressureTestId =
      this.model?.calibrations[this.selectedCalibrationIndex].pressureTestId;
    if (!pressureTestId) {
      var dialog = this.dialog.open(SelectForPressureDialogComponent, {
        panelClass: "select-for-quote-dialog",
        data: {
          customerId: this.model?.form10a?.formCustomer?.customerId,
          loadcellId: this.model?.loadcell.loadcellId,
        },
      });

      dialog.afterClosed().subscribe((list) => {
        if (list) {
          this.pressureTestService
            .apiPressureTestPressureTestPost$Json({
              calibrationId:
                this.model.calibrations[this.selectedCalibrationIndex]
                  .calibrationId,
              body: list,
            })
            .subscribe((res) => {
              if (!res?.data) return;

              this.model.calibrations[
                this.selectedCalibrationIndex
              ].pressureTestId = res.data;

              var pressureTestId =
                this.model?.calibrations[this.selectedCalibrationIndex]
                  .pressureTestId;

              this.dialog.open(PrintPressureTestDailogComponent, {
                panelClass: "print-pressure-test-dialog",
                data: {
                  pressureTestId: pressureTestId,
                  calibrationId:
                    this.model.calibrations[this.selectedCalibrationIndex]
                      .calibrationId,
                  loadcellInfo: this.model,
                },
              });
            });
        }
      });
    } else {
      this.dialog.open(PrintPressureTestDailogComponent, {
        panelClass: "print-pressure-test-dialog",
        data: {
          pressureTestId: pressureTestId,
          calibrationId:
            this.model.calibrations[this.selectedCalibrationIndex]
              .calibrationId,
          loadcellInfo: this.model,
        },
      });
    }
  }

  setLastOpenedFeature(feature: LastOpenedFeature) {
    this.repairsService.apiRepairsAndCalibrationsSetLoadcellLastOpenedFeaturePost$Json({
      loadcellId: this.model?.loadcell.loadcellId,
      feature: feature,
    }).subscribe();
  }

  getStatus(status: Status) {
    return StatusHelper.getStatusName(status);
  }

  getPriorityName(priority: PriorityStatus) {
    if(priority == PriorityStatus.Priority) return 'Premium';
    return PriorityStatus[priority].toString();
  }

  // Load both active and archived options so a block referencing a since-archived option still
  // resolves its DB name (the enum fallback only covers pre-FK rows).
  private loadBlockOptions() {
    [false, true].forEach((archived) =>
      this.calibrationService
        .apiCalibrationCalibrationBlocksGet$Json({ archived })
        .subscribe((res) => {
          this.blockOptions = [...this.blockOptions, ...(res.data ?? [])];
          this.cdr.detectChanges();
        })
    );
  }

  getBlockName(block: CalibrationBlockDto) {
    // DB option is the source of truth; fall back to the legacy enum name for pre-migration rows.
    const option = this.blockOptions.find((o) => o.id == block?.calibrationBlockOptionId);
    if (option) return option.name;

    return block?.block != null ? CalibrationBlockType[block.block]?.toString() ?? '' : '';
  }

  getBlockNames(blocks: CalibrationBlockDto[]) {
    return blocks.map(x => this.getBlockName(x)).join(', ');
  }

  getBlockIds(blocks: CalibrationBlockDto[]) {
    return blocks.map(x => x.blockNumber).join(', ');
  }

  updateStatus($event: any) {
    this.model.loadcell.status = $event.value;
    this.updateLoadcell();
  }

  updateComments($event: any) {
    this.model.loadcell.comments = $event.target.value;
    this.updateLoadcell();
  }

  updateLoadcell() {
    var user = this.appAuth.getUser();
    this.repairsService
      .apiRepairsAndCalibrationsEditLoadcellsPost$Json({ body: [this.model?.loadcell], userName: `${user.firstName} ${user.lastName}`, siteOrigin: SiteOrigin.LoadcellInformation })
      .subscribe((res) => {
        if (res?.data && res?.data?.length) {
          this.model.loadcell = res?.data[0];
          this.cdr.detectChanges();
        }
      });
  }

  updateSendReminder(calibration: CalibrationInformation, event: any) {
    calibration.sendReminder = event.checked;

    var calibrationModel: CalibrationResponse = {
      loadcellId: this.model.loadcell.loadcellId,
      calibration: calibration,
    };

    this.calibrationService
      .apiCalibrationEditCalibrationPost$Json({ body: calibrationModel })
      .subscribe((res) => {
        console.log(res);
      });
  }

  populatePhotos() {
    if (this.model.form10a.form10aId == null) {
      return;
    }
    var blobUrl: SafeUrl;
    this.files = [];
    this.urls = [];

    this.loaded = false;
    this.loadCellPhotoService
      .apiLoadCellPhotoForm10APhotosGet$Json({ form10aId: this.model.form10a.form10aId })
      .subscribe((res) => {
        this.loadCellPhotos = res;
        res.forEach((loadcell) => {
          this.loadCellPhotoService
            .apiLoadCellPhotoGetImageLoadCellPhotoIdGet$Response({ loadCellPhotoId: loadcell.id })
            .subscribe((res) => {
              var blob = new Blob([res.body]);
              var file = new File([blob], loadcell.name);

              blobUrl = this.sanitizer.bypassSecurityTrustUrl(
                URL.createObjectURL(blob)
              );
              this.files.push(file);
              this.urls.push(blobUrl);
            });
        });

        this.loaded = true;
        this.cdr.detectChanges();
      });
  }

  showSlides(n) {
    this.slideIndex = showSlidesInImageCarousel(n, this.slideIndex);
  }

  deleteImage(i: number) {
    var deleted = this.files[i];
    var photoId = this.loadCellPhotos.find((x) => x.name == deleted.name).id;
    this.loadCellPhotoService
      .apiLoadCellPhotoDeleteImageLoadCellPhotoIdDelete$Json({ loadCellPhotoId: photoId! })
      .subscribe((res) => {
        this.loaded = false;
        this.populatePhotos();
      });
  }

  downloadImage(i: number) {
    var file = this.files[i];
    var blob: Blob = file;

    // Download the blob
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name + (!file?.name.endsWith(file?.type) ? file?.type : '');
    link.click();
    window.URL.revokeObjectURL(url);
  }

  getFileSizeText(bytes: number, dp = 1) {
    if (!Number.isFinite(bytes) || bytes < 0)
      return '0 B';

    if (bytes === 0)
      return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    return `${value.toFixed(dp)} ${sizes[i]}`;
  }

  getFileCategory(fileType: string): 'document' | 'image' | 'spreadsheet' {
    if (!fileType)
      return 'document';

    const images = new Set([ 'png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg', 'tiff' ]);
    const documents = new Set([ 'pdf', 'doc', 'docx', 'txt', 'rtf', 'odt' ]);
    const spreadsheets = new Set([ 'xls', 'xlsx', 'csv', 'ods' ]);

    const type = fileType.replace('.', '').trim().toLowerCase();

    if (images.has(type))
      return 'image';

    if (documents.has(type))
      return 'document';

    if (spreadsheets.has(type))
      return 'spreadsheet';

    return 'document';
  }

  updateInvoiceAddress($event: any) {
    this.model.selectedCustomerLocationInvoice = $event.value;
    this.repairsService
      .apiRepairsAndCalibrationsUpdateInvoiceAddressPost$Json({
        customerLocationId:
          this.model?.selectedCustomerLocationInvoice?.customerLocationId,
        loadcellId: this.model?.loadcell?.loadcellId,
      })
      .subscribe();
    this.cdr.detectChanges();
  }

  getSelectedInvoiceAddress() {
    return this.model.customerLocationOptions?.find(
      (x) =>
        x.customerLocationId ==
        this.model?.selectedCustomerLocationInvoice?.customerLocationId
    );
  }

  updateDispatchAddress($event: any) {
    this.model.selectedCustomerLocationDispatch = $event.value;
    this.repairsService
      .apiRepairsAndCalibrationsUpdateDispatchAddressPost$Json({
        customerLocationId:
          this.model?.selectedCustomerLocationDispatch?.customerLocationId,
        loadcellId: this.model?.loadcell?.loadcellId,
      })
      .subscribe();
    this.cdr.detectChanges();
  }

  getSelectedDispatchAddress() {
    return this.model?.customerLocationOptions?.find(
      (x) =>
        x.customerLocationId ==
        this.model?.selectedCustomerLocationDispatch?.customerLocationId
    );
  }

  updateQuoteContact($event: any) {
    this.model.selectedCustomerContactOption = $event.value;
    this.repairsService
      .apiRepairsAndCalibrationsUpdateQuoteContactPost$Json({
        customerQuoteId: this.model?.selectedCustomerContactOption?.id,
        loadcellId: this.model?.loadcell?.loadcellId,
      })
      .subscribe();

    this.cdr.detectChanges();
  }

  getSelectedQuoteContact() {
    return this.model.customerContactOptions.find(
      (x) => x.id == this.model.selectedCustomerContactOption?.id
    );
  }

  // getNonOtherFormExaminationFaults() {
  //   return this.model.form10a.formExaminationFaults.filter(x => x.examinationFaultOption.name != 'other')
  // }

  navigateToForm10A() {
    this.router.navigate(["/calibration/form-10a/form-10a"], {
      queryParams: {
        loadcellId: this.model?.loadcell?.loadcellId,
      },
    });

    this.close();
  }

  archiveLoadcell() {
    const confirmation = this._fuseConfirmationService.open({
        title  : "Delete Loadcell",
        message: 'Are you sure you want to delete this loadcell?',
        actions: {
            confirm: {
              label: "Delete",
            },
        },
      });

      confirmation.afterClosed().subscribe((result) => {
        if ( result === 'confirmed' )
        {
          this.repairsService.apiRepairsAndCalibrationsDeleteLoadcellPost$Json$Response({body: this.model.loadcell}).subscribe(res => {
            if(!res?.body?.data) return;
            this.close();
          })
        }
      });
  }

  close() {
    this.dialogRef.close();
  }

  calculatePartsTotal(formParts: FormPartDto[]) {
    return sumList(formParts.filter(x => x.partOption != null && !!x.formPartId).map(part =>
      (part.partOption?.number == "3rd Party" ? part.cost : part.partOption?.cost) * (part.partOption.markup + 1) * part.quantity
    ));
  }

  // Files
  loadFiles() {
      if(!this.model.loadcell.loadcellId) return;

      this.loadCellPhotoService.apiLoadCellPhotoGetFilesGet$Json({ loadcellId: this.model.loadcell.loadcellId }).subscribe(res => {
          if(!res?.data) return;

          this.attachments = res.data;

          this.cdr.detectChanges();
      });
  }

  async openFileUpload(){
      var dialog = this.dialog.open(UploadFilePopupComponent, {autoFocus: false});

      dialog.afterClosed().subscribe((dialogRes) => {
        if (!dialogRes) return;

        this.uploadImages(dialogRes);
      });
  }

  deleteFile(file: LoadcellFileDto, event: any){
      event.stopPropagation();

      // Open the confirmation dialog
      const confirmation = this._fuseConfirmationService.open({
        title  : "Delete File",
        message: 'Are you sure you want to delete this file?',
        actions: {
            confirm: {
              label: "Delete",
            },
        },
      });

      // Subscribe to the confirmation dialog closed action
      confirmation.afterClosed().subscribe((result) => {
        // If the confirm button pressed...
        if ( result === 'confirmed' )
        {
          this.loadCellPhotoService.apiLoadCellPhotoDeleteFileFileIdDelete$Json({ fileId: file?.loadcellFileId }).subscribe((res) => {
              if(!res) return;

              this.loadFiles();
          });
        }
      });
    }

  async uploadImages(files: FileList) {
      if (!this.model.loadcell.loadcellId) return;

      var newFiles = Array.from(files);

      newFiles
          .forEach((file) => {
              var imageFormat = file.name!.split(".").pop()?.trim()?.toLowerCase();
              var blob: Blob = file;
              var name: string = file.name;

              this.loadCellPhotoService.apiLoadCellPhotoUploadFileLoadcellIdPost$Json({
                  loadcellId: this.model.loadcell.loadcellId,
                  filename: name,
                  body: { formFile: blob },
              }).subscribe((res) => {
                  this.loadFiles();
              });
          });
  }

  download(file: LoadcellFileDto){
      this.loadCellPhotoService.apiLoadCellPhotoDownloadFileFileIdGet({ fileId: file.loadcellFileId }).subscribe((res) => {
          if(!res) return;

          // Convert the response to a Blob
          const blob = new Blob([res], { type: 'application/octet-stream' });

          // Create a link element
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;

          // if the items name doesnt end with the file extension, add it
          if(!file?.name.endsWith(file?.type)){
              file.name += file?.type;
          }

          // Set the download attribute of the link to the desired file name
          link.download = file.name;
          // link.target = '_blank';

          // window.open(url);

          // Simulate a click on the link
          link.click();

          // Release the reference to the blob
          window.URL.revokeObjectURL(url);
      });
  }

  async openImageCapture() {
    var dialog = this.dialog.open(DLMUploadImageDialogComponent, {
      panelClass: "upload-image-file-dialog",
      data: { files: [] },
    });

    dialog.afterClosed().subscribe((res) => {
      if (!!res.cancel) return;

      var newFiles: File[] = res.files;

      if (newFiles == null)
        return;

      const dataTransfer = new DataTransfer();
      newFiles.forEach(file => dataTransfer.items.add(file));

      this.uploadImages(dataTransfer.files);
    });
  }


  //Comments
  getGenericComments = (comments: LoadcellCommentDto[]): GenericCommentDto[] =>
      comments?.map(x => <GenericCommentDto>{
          id: x.loadcellCommentId,
          parentId: x.loadcellId,
          text: x.text,
          userId: x.userId,
          timeStamp: new Date(x.timeStamp)});

  addComment(comment: GenericCommentDto) {
      var loadcellComment = <LoadcellCommentDto>{
          loadcellId: comment.parentId,
          userId: comment.userId,
          text: comment.text,
          timeStamp: comment.timeStamp.toISOString(),
      };

      this.repairsService.apiRepairsAndCalibrationsAddCommentPost$Json$Response({body: loadcellComment}).subscribe(res => {
          if(!res?.body?.data) return;

          this.model.loadcell.userComments.push(res.body.data);

          this.genericComments = this.getGenericComments(this.model.loadcell.userComments);

          this.cdr.detectChanges();
      });
  }

  updateComment(comment: GenericCommentDto) {
    var loadcellComment = <LoadcellCommentDto>{
        loadcellCommentId: comment.id,
        loadcellId: comment.parentId,
        userId: comment.userId,
        text: comment.text,
        timeStamp: comment.timeStamp.toISOString(),
    };

    this.repairsService.apiRepairsAndCalibrationsUpdateCommentPut$Json$Response({body: loadcellComment}).subscribe(res => {
        if(!res?.body?.data) return;

        var loadcellToChange = this.model.loadcell.userComments.find(x => x.loadcellCommentId == loadcellComment.loadcellCommentId);
        loadcellToChange.text = loadcellComment.text;
        loadcellToChange.timeStamp = loadcellComment.timeStamp;

        this.genericComments = this.getGenericComments(this.model.loadcell.userComments);

        this.cdr.detectChanges();
    });
  }

  removeComment(id: number) {
    this.repairsService.apiRepairsAndCalibrationsDeleteCommentDelete$Json$Response({commentId: id}).subscribe(res => {
        if(!res?.body?.data) return;

        var indexToRemove = this.model.loadcell.userComments.findIndex(x => x.loadcellCommentId == id);
        this.model.loadcell.userComments.splice(indexToRemove, 1);

        this.genericComments = this.getGenericComments(this.model.loadcell.userComments);

        this.cdr.detectChanges();
    });
  }
}
